import React from "react";
import { ArrowRightIcon } from "lucide-react";
import { z } from "zod";
import { projectOutlineSchema } from "tekkr-common/dist/model/project/project";
import { cn, positions } from "../../../lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion";
import { ProjectWorkstreamHeader } from "./workstream-header";
import { iconForDeliverable } from "../../../pages/create-project-chat/create-project-chat-page";
import { computeDiffHighlighting, ProjectDiff } from "tekkr-common/src/model/project/project-iteration";

type ProjectOutline = z.infer<typeof projectOutlineSchema>;

export function ProjectOutlineView(props: {
    plan: ProjectOutline;
    currentPhase?: number;
    chatId?: string;
    reviseProjectId?: string;
    diff?: ProjectDiff;
}) {
    const plan = props.plan;
    const highlighting = props.diff ? computeDiffHighlighting(plan, props.diff) : undefined;

    return (
        <div>
            <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
                {plan.workstreams.map((ws, i) => {
                    const { first, last } = positions(plan.workstreams, i);
                    return (
                        <AccordionItem
                            key={i}
                            value={`phase-${i}`}
                            className={cn(
                                "!border-b border-l border-r",
                                first && "rounded-t-md border-t",
                                last && "rounded-b-md",
                                highlighting?.workstreams[ws.id] === "deleted" && "bg-destructive/20 line-through",
                                highlighting?.workstreams[ws.id] === "updated" && "bg-blue-700/10",
                                highlighting?.workstreams[ws.id] === "created" && "bg-confirmation/20"
                            )}
                        >
                            <AccordionTrigger className={"px-4"}>
                                <ProjectWorkstreamHeader active={props.currentPhase === i} ws={ws} index={i} />
                            </AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-4 text-balance ps-14">
                                <p className={"text-muted-foreground"}>{ws.description}</p>
                                <hr />
                                <p className={"text-base font-semibold"}>Deliverables</p>
                                <div className={"flex flex-col gap-2"}>
                                    {ws.deliverables.map((deliverable, j) => {
                                        const Icon = iconForDeliverable(deliverable) ?? ArrowRightIcon;
                                        return (
                                            <div key={j} className={"flex flex-col gap-1"}>
                                                <div
                                                    className={"flex flex-row items-center gap-2 text-sm font-semibold"}
                                                >
                                                    <Icon className={"h-4 w-4"} />
                                                    {deliverable.name}
                                                </div>
                                                <div className={"col-span-5 mb-2 text-muted-foreground"}>
                                                    {deliverable.description}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
}
